import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { Target, Plus, Check, Edit2, Trash2, MessageSquare, ChevronDown, ChevronUp, Calendar, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All available skills (same taxonomy as Technical Development)
const ALL_SKILLS = [
  { category: 'TECHNICAL', color: 'text-emerald-400', skills: [
    { key: 'ball_control', label: 'Ball Control' },
    { key: 'passing', label: 'Passing' },
    { key: 'dribbling', label: 'Dribbling' },
    { key: 'first_touch', label: 'First Touch' },
    { key: 'finishing', label: 'Finishing' },
    { key: 'shooting', label: 'Shooting' },
  ]},
  { category: 'PHYSICAL', color: 'text-blue-400', skills: [
    { key: 'speed', label: 'Speed' },
    { key: 'acceleration', label: 'Acceleration' },
    { key: 'agility', label: 'Agility' },
    { key: 'coordination', label: 'Coordination' },
    { key: 'balance', label: 'Balance' },
    { key: 'explosiveness', label: 'Explosiveness' },
    { key: 'endurance', label: 'Endurance' },
  ]},
  { category: 'TACTICAL / IQ', color: 'text-yellow-400', skills: [
    { key: 'positioning', label: 'Positioning' },
    { key: 'decision_making', label: 'Decision Making' },
    { key: 'vision', label: 'Vision' },
    { key: 'game_awareness', label: 'Game Awareness' },
    { key: 'reaction_time', label: 'Reaction Time' },
  ]}
];

const SKILL_MAP = ALL_SKILLS.flatMap(cat => cat.skills).reduce((acc, skill) => {
  acc[skill.key] = skill.label;
  return acc;
}, {});

const PlayerGoals = () => {
  const { getAuthHeaders } = useAuth();
  const { athlete } = useAthlete();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedGoal, setExpandedGoal] = useState(null);
  const [assessments, setAssessments] = useState({});
  const [savingAssessment, setSavingAssessment] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    why_this_goal_matters: '',
    selected_skills: [],
    target_date: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (athlete) {
      fetchGoals();
    }
  }, [athlete]);

  const fetchGoals = async () => {
    if (!athlete) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/athletes/${athlete.id}/goals`, { headers: getAuthHeaders() });
      setGoals(res.data);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    }
    setLoading(false);
  };

  const fetchAssessments = async (goalId) => {
    try {
      const res = await axios.get(`${API}/athletes/${athlete.id}/goals/${goalId}/assessments`, { headers: getAuthHeaders() });
      setAssessments(prev => ({ ...prev, [goalId]: res.data }));
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
    }
  };

  const handleToggleGoal = async (goalId) => {
    if (expandedGoal === goalId) {
      setExpandedGoal(null);
    } else {
      setExpandedGoal(goalId);
      if (!assessments[goalId]) {
        await fetchAssessments(goalId);
      }
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    if (formData.selected_skills.length === 0) {
      toast.error('Please select at least one skill');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/athletes/${athlete.id}/goals`, formData, { headers: getAuthHeaders() });
      toast.success('Goal created successfully!');
      setShowCreateForm(false);
      setFormData({ title: '', why_this_goal_matters: '', selected_skills: [], target_date: '' });
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create goal');
    }
    setSubmitting(false);
  };

  const handleToggleSkill = (skillKey) => {
    setFormData(prev => ({
      ...prev,
      selected_skills: prev.selected_skills.includes(skillKey)
        ? prev.selected_skills.filter(k => k !== skillKey)
        : [...prev.selected_skills, skillKey]
    }));
  };

  const handleCompleteGoal = async (goalId) => {
    try {
      await axios.put(`${API}/athletes/${athlete.id}/goals/${goalId}`, { status: 'completed' }, { headers: getAuthHeaders() });
      toast.success('Goal marked as completed!');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    try {
      await axios.delete(`${API}/athletes/${athlete.id}/goals/${goalId}`, { headers: getAuthHeaders() });
      toast.success('Goal deleted');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const handleSaveAssessment = async (goalId, skillKey, level, notes) => {
    setSavingAssessment(`${goalId}-${skillKey}`);
    try {
      await axios.post(`${API}/athletes/${athlete.id}/goals/${goalId}/assessments`, {
        skill_key: skillKey,
        level,
        notes
      }, { headers: getAuthHeaders() });
      toast.success('Assessment saved');
      fetchAssessments(goalId);
    } catch (error) {
      toast.error('Failed to save assessment');
    }
    setSavingAssessment(null);
  };

  const getStatusColor = (status) => {
    const s = (status || '').toString().toLowerCase();
    if (s === 'proficient') return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (s === 'developing') return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    if (s === 'focus') return 'bg-red-500/20 text-red-400 border-red-500/30';
    return 'bg-muted text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="player-goals">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">My Goals & Self-Assessment</h1>
          <p className="text-muted-foreground">{athlete?.name}'s personal development goals</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="bg-primary text-black hover:bg-primary/90">
          <Plus className="mr-2 h-4 w-4" />
          New Goal
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-sm">
        <p className="text-sm text-blue-400">
          <strong>Player Self-Assessment:</strong> This is your space to set personal goals and track your own progress. 
          Your self-ratings are separate from coach evaluations and help you reflect on your development journey.
        </p>
      </div>

      {/* Create Goal Form */}
      {showCreateForm && (
        <div className="bg-card border border-primary/30 p-6 rounded-sm">
          <h2 className="font-heading text-xl uppercase mb-4">Create New Goal</h2>
          <form onSubmit={handleCreateGoal} className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Goal Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Improve my first touch"
                required
              />
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Why This Goal Matters</label>
              <Textarea
                value={formData.why_this_goal_matters}
                onChange={(e) => setFormData({ ...formData, why_this_goal_matters: e.target.value })}
                placeholder="Explain why this goal is important to you..."
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Target Date (Optional)</label>
              <Input
                type="date"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Select Skills to Focus On</label>
              <div className="space-y-4">
                {ALL_SKILLS.map(category => (
                  <div key={category.category}>
                    <p className={`text-xs uppercase tracking-wider mb-2 ${category.color}`}>{category.category}</p>
                    <div className="flex flex-wrap gap-2">
                      {category.skills.map(skill => (
                        <button
                          key={skill.key}
                          type="button"
                          onClick={() => handleToggleSkill(skill.key)}
                          className={`px-3 py-1 text-sm rounded-sm border transition-colors ${
                            formData.selected_skills.includes(skill.key)
                              ? 'bg-primary text-black border-primary'
                              : 'bg-transparent border-border hover:border-primary/50'
                          }`}
                        >
                          {skill.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-primary text-black">
                {submitting ? 'Creating...' : 'Create Goal'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Goals List */}
      {goals.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <Target className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-2">No goals yet</p>
          <p className="text-sm text-muted-foreground mb-4">
            Start by creating your first personal development goal
          </p>
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create First Goal
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {goals.map(goal => (
            <GoalCard
              key={goal.id}
              goal={goal}
              expanded={expandedGoal === goal.id}
              onToggle={() => handleToggleGoal(goal.id)}
              onComplete={() => handleCompleteGoal(goal.id)}
              onDelete={() => handleDeleteGoal(goal.id)}
              assessments={assessments[goal.id] || []}
              onSaveAssessment={handleSaveAssessment}
              savingAssessment={savingAssessment}
              getStatusColor={getStatusColor}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Goal Card Component
const GoalCard = ({ goal, expanded, onToggle, onComplete, onDelete, assessments, onSaveAssessment, savingAssessment, getStatusColor }) => {
  const [skillAssessments, setSkillAssessments] = useState({});

  // Get latest assessment for each skill
  const getLatestAssessment = (skillKey) => {
    const skillAssessments = assessments.filter(a => a.skill_key === skillKey);
    return skillAssessments[0] || null;
  };

  const handleLevelChange = (skillKey, level) => {
    setSkillAssessments(prev => ({
      ...prev,
      [skillKey]: { ...prev[skillKey], level }
    }));
  };

  const handleNotesChange = (skillKey, notes) => {
    setSkillAssessments(prev => ({
      ...prev,
      [skillKey]: { ...prev[skillKey], notes }
    }));
  };

  const handleSave = (skillKey) => {
    const assessment = skillAssessments[skillKey] || {};
    const latest = getLatestAssessment(skillKey);
    onSaveAssessment(
      goal.id,
      skillKey,
      assessment.level || latest?.level || 'Focus',
      assessment.notes || ''
    );
  };

  return (
    <div className={`bg-card border rounded-sm overflow-hidden ${goal.status === 'completed' ? 'border-green-500/30' : 'border-border/40'}`}>
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/20 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            goal.status === 'completed' ? 'bg-green-500/20' : 'bg-primary/10'
          }`}>
            {goal.status === 'completed' ? (
              <Check className="text-green-400" size={20} />
            ) : (
              <Target className="text-primary" size={20} />
            )}
          </div>
          <div>
            <h3 className="font-semibold">{goal.title}</h3>
            <p className="text-xs text-muted-foreground">
              {goal.selected_skills.length} skills • Created by {goal.created_by}
              {goal.target_date && ` • Target: ${new Date(goal.target_date).toLocaleDateString()}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {goal.status === 'completed' && (
            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">Completed</span>
          )}
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-border/40 p-4 space-y-4">
          {/* Why This Goal Matters */}
          {goal.why_this_goal_matters && (
            <div className="bg-muted/20 p-3 rounded-sm">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Why This Matters</p>
              <p className="text-sm">{goal.why_this_goal_matters}</p>
            </div>
          )}

          {/* Coach Comment */}
          {goal.coach_comment && (
            <div className="bg-primary/10 border border-primary/30 p-3 rounded-sm">
              <p className="text-xs text-primary uppercase tracking-wider mb-1 flex items-center gap-1">
                <MessageSquare size={12} />
                Coach Comment
              </p>
              <p className="text-sm">{goal.coach_comment}</p>
            </div>
          )}

          {/* Self-Assessment Section */}
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wider mb-3">Self-Assessment (Player/Parent Rating)</p>
            <div className="space-y-3">
              {goal.selected_skills.map(skillKey => {
                const latest = getLatestAssessment(skillKey);
                const currentLevel = skillAssessments[skillKey]?.level || latest?.level || '';
                
                return (
                  <div key={skillKey} className="bg-muted/20 p-3 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{SKILL_MAP[skillKey] || skillKey}</span>
                      <div className="flex gap-1">
                        {['Focus', 'Developing', 'Proficient'].map(level => (
                          <button
                            key={level}
                            onClick={() => handleLevelChange(skillKey, level)}
                            className={`px-2 py-0.5 text-xs rounded ${
                              currentLevel === level
                                ? getStatusColor(level)
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Notes about difficulty or improvement..."
                        className="bg-transparent text-sm flex-1"
                        rows={2}
                        defaultValue={latest?.notes || ''}
                        onChange={(e) => handleNotesChange(skillKey, e.target.value)}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleSave(skillKey)}
                        disabled={savingAssessment === `${goal.id}-${skillKey}`}
                        className="bg-primary text-black h-auto"
                      >
                        <Save size={14} />
                      </Button>
                    </div>
                    {latest && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Last updated: {new Date(latest.created_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Assessment History */}
          {assessments.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">Recent History</p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {assessments.slice(0, 10).map(a => (
                  <div key={a.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{new Date(a.created_at).toLocaleDateString()}</span>
                    <span className={`px-1.5 py-0.5 rounded ${getStatusColor(a.level)}`}>{a.level}</span>
                    <span>{SKILL_MAP[a.skill_key]}</span>
                    {a.notes && <span className="truncate">- {a.notes}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {goal.status !== 'completed' && (
            <div className="flex gap-2 pt-2 border-t border-border/40">
              <Button size="sm" variant="outline" onClick={onComplete} className="flex-1">
                <Check className="mr-1 h-4 w-4" />
                Mark Complete
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-400 hover:text-red-300">
                <Trash2 size={16} />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerGoals;
